/**
 * Reading a stored program: its shape settled, and the questions the panes ask
 * of it.
 *
 * The picture is **not** here. It used to be — this module inferred the canvas
 * from the engine's fields, working backwards from a command's ordered
 * `validations` and `triggers` to a shape — and that inference was the source
 * of a whole family of bugs, because one node can be several cards and the
 * fields cannot say which card a line joined. The picture is data now, in
 * `graph.ts`, and the engine's fields are computed *from* it by the server.
 *
 * What is left is what a program means rather than how it looks: normalizing a
 * half-built draft into something safe to read, the lanes the Layers pane
 * lists, and the arithmetic of panning and zooming.
 *
 * The one ordering that is meaningful and not ours to invent: triggers run in
 * the order the engine computed from their `reads`, which arrives on the
 * document as `triggerOrder`.
 */

import type {
  AnyNode,
  CommandNode,
  ContextNode,
  ComputeNode,
  NodeKind,
  ScoringProgram,
  TriggerNode,
  ValidationNode,
  Violation,
} from "./types";

/**
 * A stored document as the canvas can rely on it.
 *
 * A document is not required to carry a section it has nothing in — an empty
 * draft is `{"sport": "cricket"}` and nothing more, and that is deliberate: the
 * draft model is permissive so a half-built program can be saved at all. The
 * canvas is not permissive, because every read of `program.commands` would
 * otherwise need a guard, and the one that gets forgotten is a blank screen
 * with a `TypeError` behind it.
 *
 * So the shape is settled once, here, at the edge — the same job a parser does.
 */
const list = <T>(value: T[] | undefined | null): T[] => (Array.isArray(value) ? value : []);

/**
 * One node, with every optional field present.
 *
 * The same job as `normalize` and for the same reason, one level down: a node
 * the API just created carries only what it was given, so `appliesTo` and
 * `reads` and a command's `validations` are simply absent until something sets
 * them. Every one of those is read unguarded by the canvas.
 */
function node<T extends Record<string, unknown>>(raw: T, kind: NodeKind): T {
  const ui =
    (raw.ui as { x?: number; y?: number; collapsed?: boolean; hidden?: boolean } | undefined) ?? {};
  const common = {
    ...raw,
    id: (raw.id as string) || (raw.code as string) || "",
    code: (raw.code as string) || (raw.id as string) || "",
    label: (raw.label as string | null) ?? null,
    description: (raw.description as string | null) ?? null,
    rev: (raw.rev as number) ?? 0,
    ui: { x: ui.x ?? 0, y: ui.y ?? 0, collapsed: ui.collapsed ?? false, hidden: ui.hidden ?? false },
    appliesTo: list(raw.appliesTo as string[]),
    parent: (raw.parent as string | null) ?? null,
  };
  if (kind === "command") {
    return {
      ...common,
      op: (raw.op as string) || "append",
      input: (raw.input as object) ?? {},
      actors: list(raw.actors as unknown[]),
      validations: list(raw.validations as string[]),
      // Programs now call these `actions`; a document saved before that says
      // `triggers`. Reading only the old name drew every action as missing.
      triggers: list((raw.actions ?? raw.triggers) as string[]),
    } as unknown as T;
  }
  const withCode = { ...common, fn: (raw.fn as string | null) ?? null, tests: list(raw.tests as unknown[]) };
  if (kind === "validation") {
    // A document written before the list existed says `expr`. The server folds
    // it too; this is here because the canvas also reads drafts straight off a
    // save response, which has not been through the server's reader.
    const legacy = raw.expr as string | null | undefined;
    const checks = list(raw.checks as unknown[]);
    return {
      ...withCode,
      message: (raw.message as string) ?? "",
      checks:
        checks.length > 0
          ? checks
          : legacy
            ? [{ expr: legacy, message: (raw.message as string) ?? "" }]
            : [],
    } as unknown as T;
  }
  if (kind === "trigger") {
    return {
      ...withCode,
      mode: (raw.mode as string) || "sync",
      scope: (raw.scope as string) || "fixture",
      scopeRef: (raw.scopeRef as string | null) ?? null,
      owns: (raw.owns as object | null) ?? null,
      reads: list(raw.reads as string[]),
    } as unknown as T;
  }
  if (kind === "context") {
    return { ...withCode, reads: list(raw.reads as string[]) } as unknown as T;
  }
  return {
    ...withCode,
    owns: (raw.owns as string) || "scoreboard",
    key: (raw.key as string) ?? "",
    parents: list(raw.parents as string[]),
    reads: list(raw.reads as string[]),
  } as unknown as T;
}

/** The one context, whichever shape the document uses to say it. */
function contextOf(
  document: Partial<ScoringProgram> | null | undefined,
  older: { context?: unknown } | null,
): ContextNode | null {
  const raw = Array.isArray(older?.context) ? older.context[0] : document?.context;
  return raw ? (node(raw as Record<string, unknown>, "context") as unknown as ContextNode) : null;
}

export function normalize(document: Partial<ScoringProgram> | null | undefined): ScoringProgram {
  const older =
    (document as { derive?: unknown; derives?: unknown; context?: unknown } | null) ?? null;
  const section = <T extends { id?: string }>(value: T[] | undefined, kind: NodeKind): T[] =>
    list(value).map((raw) => node(raw as Record<string, unknown>, kind) as unknown as T);
  return {
    sport: document?.sport ?? "",
    version: document?.version ?? "",
    commands: section(document?.commands, "command"),
    validations: section(document?.validations, "validation"),
    // This section has been called three things: `derive` (one fold), `derives`
    // (a list of them), and now `computes`, matching the decorator that writes
    // them. The server normalizes on read, but a document can also arrive from
    // an export or a fixture file older than that — so all three are read here,
    // exactly as `ScoringProgram._accept_the_older_shape` does server-side.
    computes: section(
      document?.computes ??
        (older?.derives as ComputeNode[] | undefined) ??
        ((older?.derive ? [older.derive] : []) as ComputeNode[]),
      "compute",
    ),
    triggers: section(
      ((document as { actions?: TriggerNode[] } | null)?.actions ?? document?.triggers) as
        | TriggerNode[]
        | undefined,
      "trigger",
    ),
    context: contextOf(document, older),
    // The engine's, not ours: it arrives on the document when the server has
    // computed it, and is empty until then.
    triggerOrder: list(document?.triggerOrder),
    violations: list(document?.violations),
    // The picture, carried through untouched. Normalizing rebuilds the
    // document field by field, so anything not named here is dropped — and a
    // canvas dropped on the way in is a builder that opens empty over a full
    // program.
    canvas: document?.canvas ?? null,
  };
}

export interface Lane {
  command: ScoringProgram["commands"][number];
  validations: ValidationNode[];
  triggers: TriggerNode[];
  /** Validations or triggers the command names that no node declares. */
  missing: string[];
}

const byId = <T extends AnyNode>(nodes: T[]): Map<string, T> =>
  new Map(nodes.map((n) => [n.id, n]));

export function lanes(program: ScoringProgram): Lane[] {
  const validations = byId(program.validations);
  const triggers = byId(program.triggers);
  const order = new Map(program.triggerOrder.map((id, i) => [id, i]));

  return program.commands.map((command) => {
    const missing: string[] = [];
    const resolved = <T extends AnyNode>(refs: string[], from: Map<string, T>): T[] =>
      refs.flatMap((ref) => {
        const node = from.get(ref);
        if (!node) missing.push(ref);
        return node ? [node] : [];
      });

    return {
      command,
      // Declaration order: the author dragged them into this order on purpose.
      validations: resolved(command.validations, validations),
      // Dependency order: fight totals cannot run before the round stats they read.
      triggers: resolved(command.triggers, triggers).sort(
        (a, b) => (order.get(a.id) ?? 0) - (order.get(b.id) ?? 0),
      ),
      missing,
    };
  });
}

/**
 * Triggers no command fires. Not an error — a competition-scoped table is fired
 * by the fixtures beneath it — but worth showing rather than hiding, because
 * the other reason a trigger is unreferenced is that someone forgot.
 */
export function unattached(program: ScoringProgram): TriggerNode[] {
  const fired = new Set(program.commands.flatMap((c) => c.triggers));
  return program.triggers.filter((t) => !fired.has(t.id));
}

/** A component and its kind — what the Components pane lists and drags. */
export interface Component {
  kind: "validation" | "compute" | "trigger";
  node: ValidationNode | ComputeNode | TriggerNode;
}

/**
 * Every node the author declared as shared, newest last.
 *
 * Reads the flag rather than counting commands, which is the whole point of the
 * flag: a component that has just been made is used nowhere, and a list built
 * from usage would not show you the thing you are about to attach.
 */
export function components(program: ScoringProgram): Component[] {
  return [
    ...program.validations.map((node) => ({ kind: "validation" as const, node })),
    ...program.computes.map((node) => ({ kind: "compute" as const, node })),
    ...program.triggers.map((node) => ({ kind: "trigger" as const, node })),
  ].filter((entry) => entry.node.component === true);
}

/**
 * Which commands run this node — the flows that change if you edit it.
 *
 * A fold is the exception and it is not a special case so much as the truth
 * about folds: nothing references one, because every fold runs on every
 * command. Answering "all of them" here would be a lie by shape — the caller
 * needs to say "the whole program" in words — so this returns nothing and
 * `runsEverywhere` is what asks the real question.
 */
export function runsIn(program: ScoringProgram, nodeId: string): CommandNode[] {
  return program.commands.filter(
    (command) => command.validations.includes(nodeId) || command.triggers.includes(nodeId),
  );
}

/** Whether this node runs for the whole program rather than for named commands. */
export function runsEverywhere(kind: NodeKind): boolean {
  return kind === "compute" || kind === "context";
}

/** What a node reads that another node produces — shown on the inspector. */
export function dependencies(node: TriggerNode | ContextNode): string[] {
  return node.reads.filter((r) => r.startsWith("stats.")).map((r) => r.slice("stats.".length));
}

export function violationsFor(violations: Violation[], nodeId: string): Violation[] {
  return violations.filter((v) => v.nodeId === nodeId);
}

/** Counts for the palette headings — how much of each kind a program has. */
export function tally(program: ScoringProgram): Record<string, number> {
  return {
    command: program.commands.length,
    validation: program.validations.length,
    compute: program.computes.length,
    trigger: program.triggers.length,
    context: program.context ? 1 : 0,
  };
}

/** Every test case across the program, and how many nodes carry none. */
export function evidence(program: ScoringProgram): { tests: number; untested: number } {
  const carriers: { fn: string | null; tests: unknown[] }[] = [
    ...program.validations,
    ...program.computes,
    ...program.triggers,
    ...(program.context ? [program.context] : []),
  ];
  const withCode = carriers.filter((n) => n.fn);
  return {
    tests: withCode.reduce((sum, n) => sum + n.tests.length, 0),
    untested: withCode.filter((n) => n.tests.length === 0).length,
  };
}

export const ROW_GAP = 160;

/* ------------------------------------------------------------ pan and zoom */

/**
 * The canvas is infinite, so there is no scroll — there is a **pan**.
 *
 * A scroll box can only reach the content it already has, which is why the old
 * one had scrollbars and edges: you could not move past the last node, and a
 * node could not be put above or to the left of the first one, because there
 * were no negative coordinates to put it at. A pan has no edges and no bars.
 *
 * The whole model is one line, and everything below is algebra on it:
 *
 *     screen = world × zoom + pan
 *
 * so `world = (screen − pan) / zoom`. A node's stored `ui.x/y` is a world
 * coordinate and never changes when you move around.
 */
export interface Pan {
  x: number;
  y: number;
}

/** The world point under a point on screen. */
export function toWorld(at: { x: number; y: number }, pan: Pan, zoom: number): Pan {
  return { x: (at.x - pan.x) / zoom, y: (at.y - pan.y) / zoom };
}

/**
 * The pan that leaves one point exactly where it was while the scale changes.
 *
 * Without it the canvas zooms about its top-left corner and whatever you were
 * looking at slides away — the difference between zooming *in on* something and
 * merely making everything bigger.
 *
 * Pure, and here rather than in the component, because it is the one part of
 * panning and zooming that can be wrong in a way you cannot see by looking.
 */
export function panForZoom(pan: Pan, at: { x: number; y: number }, from: number, to: number): Pan {
  const scale = to / from;
  return {
    x: at.x - (at.x - pan.x) * scale,
    y: at.y - (at.y - pan.y) * scale,
  };
}

/**
 * The pan that brings everything into view, and the zoom to see it at.
 *
 * Fits the nodes' own bounding box rather than a world size, because on an
 * infinite canvas there is no world size — only wherever the nodes happen to
 * be, which may be a long way from the origin in any direction.
 */
export function fitToView(
  boxes: { x: number; y: number }[],
  card: { width: number; height: number },
  view: { width: number; height: number },
  limits: { min: number; max: number },
  padding = 64,
): { pan: Pan; zoom: number } {
  if (boxes.length === 0 || view.width === 0 || view.height === 0) {
    return { pan: { x: padding, y: padding }, zoom: 1 };
  }
  const left = Math.min(...boxes.map((b) => b.x));
  const top = Math.min(...boxes.map((b) => b.y));
  const right = Math.max(...boxes.map((b) => b.x)) + card.width;
  const bottom = Math.max(...boxes.map((b) => b.y)) + card.height;

  const zoom = Math.min(
    limits.max,
    Math.max(
      limits.min,
      Math.min(
        (view.width - padding * 2) / Math.max(1, right - left),
        (view.height - padding * 2) / Math.max(1, bottom - top),
      ),
    ),
  );
  // Centred: the leftover space is split, rather than everything pinned to a
  // corner with all the emptiness on one side.
  return {
    pan: {
      x: (view.width - (right - left) * zoom) / 2 - left * zoom,
      y: (view.height - (bottom - top) * zoom) / 2 - top * zoom,
    },
    zoom,
  };
}

export function appliesTo(node: AnyNode, variant: string | null): boolean {
  if (!variant || node.appliesTo.length === 0) return true;
  return node.appliesTo.includes(variant);
}

export function matches(node: AnyNode, query: string): boolean {
  if (!query.trim()) return true;
  const needle = query.trim().toLowerCase();
  return [node.code, node.label ?? "", node.description ?? ""]
    .join(" ")
    .toLowerCase()
    .includes(needle);
}
